"use client";

import { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Container, Paper, Typography } from '@mui/material';
import NewProjectList from '@/components/projects/NewProjectList.tsx';
import UserManagement from '@/components/home/UserManagement.tsx';
import PlansAdmin from '@/components/PlansAdmin.tsx';
import PerformanceTargetForm from '@/components/home/PerformanceTargetForm.tsx';
import MonitoringForm from '@/components/home/MonitoringForm.tsx';
import SelfEvaluationForm from '@/components/home/SelfEvaluationForm.tsx';
import ArticleIcon from '@mui/icons-material/Article';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
  [key: string]: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`main-tabpanel-${index}`}
      aria-labelledby={`main-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function getA11yProps(index: number) {
  return {
    id: `main-tab-${index}`,
    "aria-controls": `main-tabpanel-${index}`,
  };
}

// 占位符组件
interface PlaceholderComponentProps {
  title: string;
  description: string;
}

function PlaceholderComponent({
  title,
  description,
}: PlaceholderComponentProps) {
  return (
    <Box sx={{ p: 4, textAlign: "center" }}>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {description}
      </Typography>
    </Box>
  );
}

export default function HomePage() {
  const [value, setValue] = useState(0);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    // 防止点击图标/空白被阻断，强制更新索引
    setValue(Number(newValue));
  };

  // 获取当前用户角色和组织类型以决定显示哪些标签页
  let currentUserRole = null;
  let currentUserOrgType = "third_party"; // 默认为第三方公司
  if (isClient) {
    // 确保 localStorage 只在客户端被访问
    try {
      const userStr = localStorage.getItem("currentUser");
      if (userStr) {
        const userObj = JSON.parse(userStr);
        currentUserRole = userObj.role;
        currentUserOrgType = userObj.organization_type || "third_party";
        console.log("MainTabs - currentUserOrgType:", currentUserOrgType, "currentUserRole:", currentUserRole);
      }
    } catch (e) {
      // Error parsing user from localStorage
    }
  }

  const isAdmin =
    currentUserRole === "admin" ||
    currentUserRole === "zhaoyuan_admin" ||
    currentUserRole === "super_admin";
  const canManagePlans =
    currentUserRole === "super_admin" || currentUserRole === "zhaoyuan_admin";

  // 根据单位类型判断是否可以创建重点项目（只有第三方公司和昭元管理员可以创建重点项目）
  const canCreateKeyProject =
    currentUserOrgType === "third_party" || currentUserOrgType === "zhaoyuan";

  // 根据单位类型判断是否可以访问事中监控（昭元管理员和政府单位可以访问）
  const canAccessMonitoring =
     currentUserOrgType === "zhaoyuan" || currentUserOrgType === "government";

  // 根据单位类型判断是否可以访问事后自评（昭元管理员和政府单位可以访问）
  const canAccessSelfEvaluation = currentUserOrgType === "zhaoyuan" || currentUserOrgType === "government";

  // 计算动态索引
  const getUserManagementIndex = () => {
    let index = 4; // 基础索引（前4个标签页：重点项目、绩效目标申报表、事中监控、事后自评）
    return index;
  };

  const getPlanManagementIndex = () => {
    let index = 5; // 基础索引
    return index;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Paper elevation={0} sx={{ width: "100%", bgcolor: "transparent" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="main content tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {canCreateKeyProject && (
              <Tab
                label="重点项目"
                icon={<ArticleIcon />}
                iconPosition="start"
                {...getA11yProps(0)}
              />
            )}
            <Tab
              label="绩效目标申报表"
              icon={<AssessmentIcon />}
              iconPosition="start"
              {...getA11yProps(canCreateKeyProject ? 1 : 0)}
            />
            {canAccessMonitoring && (
              <Tab
                label="绩效监控表"
                icon={<MonitorHeartIcon />}
                iconPosition="start"
                {...getA11yProps(canCreateKeyProject ? 2 : 1)}
              />
            )}
            {canAccessSelfEvaluation && (
              <Tab
                label="绩效自评"
                icon={<HandshakeIcon />}
                iconPosition="start"
                {...getA11yProps(canCreateKeyProject ? (canAccessMonitoring ? 3 : 2) : (canAccessMonitoring ? 2 : 1))}
              />
            )}
            {isAdmin && (
              <Tab label="用户管理" icon={<AccountBoxIcon />} iconPosition="start" {...getA11yProps(canCreateKeyProject ? (canAccessSelfEvaluation ? (canAccessMonitoring ? 4 : 3) : 3) : (canAccessSelfEvaluation ? (canAccessMonitoring ? 3 : 2) : 2))} onClick={() => setValue(canCreateKeyProject ? (canAccessSelfEvaluation ? (canAccessMonitoring ? 4 : 3) : 3) : (canAccessSelfEvaluation ? (canAccessMonitoring ? 3 : 2) : 2))} />
            )}
            {canManagePlans && (
              <Tab label="套餐管理" icon={<ListAltIcon />} iconPosition="start" {...getA11yProps(canCreateKeyProject ? (canAccessSelfEvaluation ? (canAccessMonitoring ? 5 : 4) : 4) : (canAccessSelfEvaluation ? (canAccessMonitoring ? 4 : 3) : 3))} onClick={() => setValue(canCreateKeyProject ? (canAccessSelfEvaluation ? (canAccessMonitoring ? 5 : 4) : 4) : (canAccessSelfEvaluation ? (canAccessMonitoring ? 4 : 3) : 3))} />
            )}
          </Tabs>
        </Box>
        {canCreateKeyProject && (
          <TabPanel value={value} index={0}>
            <NewProjectList />
          </TabPanel>
        )}
        <TabPanel value={value} index={canCreateKeyProject ? 1 : 0}>
          <PerformanceTargetForm />
        </TabPanel>
        {canAccessMonitoring && (
          <TabPanel value={value} index={canCreateKeyProject ? 2 : 1}>
            <MonitoringForm />
          </TabPanel>
        )}
        {canAccessSelfEvaluation && (
          <TabPanel value={value} index={canCreateKeyProject ? (canAccessMonitoring ? 3 : 2) : (canAccessMonitoring ? 2 : 1)}>
            <SelfEvaluationForm />
          </TabPanel>
        )}
        {isAdmin && (
          <TabPanel value={value} index={canCreateKeyProject ? (canAccessSelfEvaluation ? (canAccessMonitoring ? 4 : 3) : 3) : (canAccessSelfEvaluation ? (canAccessMonitoring ? 3 : 2) : 2)}>
            <UserManagement />
          </TabPanel>
        )}
        {canManagePlans && (
          <TabPanel value={value} index={canCreateKeyProject ? (canAccessSelfEvaluation ? (canAccessMonitoring ? 5 : 4) : 4) : (canAccessSelfEvaluation ? (canAccessMonitoring ? 4 : 3) : 3)}>
            <PlansAdmin />
          </TabPanel>
        )}
      </Paper>
    </Container>
  );
}
